import React, { useContext, useEffect, useState } from "react";
import { GreenMachineContext } from "../../GreenMachineContext";
import {
  commitMutation,
  graphql,
  QueryRenderer,
  useLazyLoadQuery,
  usePaginationFragment,
} from "react-relay";
import environment from "../../../../../../RelayEnvironment";
import GreenMachineLineup from "./GreenMachineLineup";
import { gql, useLazyQuery } from "@apollo/client";
import relayEnvironment from "../../../../../../RelayEnvironment";
import { GENERATE_LINEUPS } from "../../SelectExposures/mutations/GenerateLineups";
import LoadingOverlay from "react-loading-overlay";
import { COMMIT_LINEUP_LOCKS } from "./mutations/CommitLineupLockChanges";
import { TailSpin } from "react-loader-spinner";

import "./ViewLineups.css";
import LineupSummaries from "../LineupSummaries";

const GET_LINEUP_SET_INFO = graphql`
  query ViewLineupsQuery($lineupSetID: ID!) {
    numValidLineupsForLineupSet(lineupSetId: $lineupSetID) {
      totalNumLineups
      validLineups
      yellowLineupsPresent
      redLineupsPresent
    }
  }
`;

const LINEUPS_PAGINATION = graphql`
  fragment ViewLineups_query on Query
  @argumentDefinitions(
    lineupSetId: { type: "ID!" }
    count: { type: Int, defaultValue: 9 }
    cursor: { type: String, defaultValue: null }
  )
  @refetchable(queryName: "ViewLineupsPaginationQuery") {
    lineupsForLineupSet(
      lineupSetId: $lineupSetId
      first: $count
      after: $cursor
    ) @connection(key: "ViewLineups_lineupsForLineupSet") {
      edges {
        node {
          id
          totalSalary
          totalProjOwnership
          totalProjPoints
          isValid
          validityLevel
          locked
          players {
            id
            lineupRosterSlot {
              id
              rosterSlot {
                id
                name
              }
            }
            player {
              slatePlayer {
                id
                position
                salary
                player {
                  name
                  shortenedName
                  team {
                    abbreviation
                  }
                }

                suffix
                gupPercProj
                projPoints
              }
            }
          }
        }
      }
    }
  }
`;

const UPDATE_ALL_LINEUP_LOCKS = graphql`
  mutation ViewLineupsAllLineupLockMutation(
    $input: CommitLineupLockChangesInput!
  ) {
    commitLineupLockChanges(input: $input) {
      success
    }
  }
`;

const GET_CSV_FOR_LINEUP_SET = gql`
  query GetCSVForLineupSet($lineupSetID: ID!) {
    csvForLineupSet(lineupSetId: $lineupSetID) {
      csv
    }
  }
`;

export default function ViewLineups(props) {
  const {
    greenMachineSessionID,
    selectedSlateID,
    numLineups,
    setErrorMessage,
  } = useContext(GreenMachineContext);

  const metadata = useLazyLoadQuery(GET_LINEUP_SET_INFO, {
    lineupSetID: props.lineupSetID,
  });

  const { data, error, loadNext } = usePaginationFragment(
    LINEUPS_PAGINATION,
    props.query
  );

  if (data.lineupsForLineupSet.pageInfo.hasNextPage) {
    loadNext();
  }

  const [generating, setGenerating] = useState(false);
  const [overlayText, setOverlayText] = useState(undefined);
  const [yellowLineupsPresent, setYellowLineupsPresent] = useState(false);
  const [redLineupsPresent, setRedLineupsPresent] = useState(false);

  const [getCSV] = useLazyQuery(GET_CSV_FOR_LINEUP_SET, {
    onCompleted: (csv) => {
      setGenerating(false);
      let encodedUri = encodeURI(
        "data:text/plain;charset=utf-8," + csv.csvForLineupSet.csv
      );
      let link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("class", new Date().getTime().toString());
      link.setAttribute("download", "green_machine_lineups.csv");
      document.body.appendChild(link);
      link.click();
    },
    onError: () => {
      setGenerating(false);
      setErrorMessage("Something went wrong. Please try again later.");
    },
    fetchPolicy: "network-only",
  });

  function commitLineupLockStatus(id, locked, updateState) {
    commitMutation(relayEnvironment, {
      mutation: COMMIT_LINEUP_LOCKS,
      variables: {
        input: {
          lineupLockChanges: [
            {
              gmLineupId: id,
              locked: locked,
            },
          ],
        },
      },
      onCompleted: (content, errors) => {
        if (errors) {
          setErrorMessage(errors[0].message);
          return;
        }

        setErrorMessage(undefined);

        updateState(locked);
      },
      onError: (error) => {
        setErrorMessage(error);
      },
    });
  }

  function exportToCSV(e) {
    e.preventDefault();

    if (yellowLineupsPresent || redLineupsPresent) {
      let warningMessage = "";

      if (yellowLineupsPresent && redLineupsPresent) {
        warningMessage =
          "You have YELLOW and RED lineups in your build. Are you sure you want to export with YELLOW lineups? RED lineups can not be exported and will be ignored.";
      } else if (yellowLineupsPresent) {
        warningMessage =
          "You have YELLOW lineups in your build. Are you sure you want to export with YELLOW lineups?";
      } else if (redLineupsPresent) {
        warningMessage =
          "You have RED lineups in your build. RED lineups cannot be exported and will be ignored. Would you like to continue exporting your GREEN lineups?";
      }

      if (!confirm(warningMessage)) {
        return;
      }
    }

    setGenerating(true);
    setOverlayText("Generating csv...");
    getCSV({ variables: { lineupSetID: props.lineupSetID } });
  }

  function adjustAllLineupLocks(e, lineups, locked) {
    e.preventDefault();

    commitMutation(relayEnvironment, {
      mutation: COMMIT_LINEUP_LOCKS,
      variables: {
        input: {
          lineupLockChanges: lineups.map((lineup, index) => {
            return { gmLineupId: lineup.node.id, locked: locked };
          }),
        },
      },
      onCompleted: (content, errors) => {
        if (errors) {
          setErrorMessage(errors[0].message);
          return;
        }

        setErrorMessage(undefined);

        props.refetch(false);
      },
      onError: (error) => {
        console.log(error);
      },
    });
  }

  function regenerateLineups(e) {
    e.preventDefault();

    setErrorMessage(undefined);
    setGenerating(true);
    setOverlayText("Generating lineups...");

    commitMutation(relayEnvironment, {
      mutation: GENERATE_LINEUPS,
      variables: {
        input: {
          greenMachineSessionId: greenMachineSessionID,
          regenerateFromLineupSetId: props.lineupSetID,
        },
      },
      onCompleted: (content, errors) => {
        props.refetch(true);
        setGenerating(false);

        if (errors) {
          setErrorMessage(errors[0].message);
        }
      },
      onError: (error) => {
        setGenerating(false);

        setErrorMessage(error);
      },
    });
  }

  function reloadLineups(lineupEdit) {
    props.refetch(lineupEdit);
  }

  if (error) {
    return <div>Something went wrong.</div>;
  }

  useEffect(() => {
    setYellowLineupsPresent(
      metadata.numValidLineupsForLineupSet.yellowLineupsPresent
    );
    setRedLineupsPresent(
      metadata.numValidLineupsForLineupSet.redLineupsPresent
    );
  }, [props.lineupSetID]);

  if (!props) {
    setYellowLineupsPresent(false);
    setRedLineupsPresent(false);
    return (
      <div className="gm-view-lineups-header-container">Loading lineups...</div>
    );
  }

  const validLineups = metadata.numValidLineupsForLineupSet.validLineups;

  if (validLineups < numLineups) {
    let error = `Could only build ${validLineups} valid lineups.`;

    const totalLineups = metadata.numValidLineupsForLineupSet.totalNumLineups;

    if (totalLineups < numLineups) {
      error +=
        " Try expanding your player pool or loosening your salary restrictions and regenerating.";
    } else {
      error += ` ${
        totalLineups - validLineups
      } lineups that did not meet your specifications have been included for editing and marked in yellow/red.`;
    }

    setErrorMessage(error);
  }

  return (
    <div>
      <div className="gm-view-lineups-header-container">
        <LoadingOverlay spinner text={overlayText} active={generating}>
          <button onClick={(e) => exportToCSV(e)}>Export Lineups To CSV</button>{" "}
          <button onClick={(e) => regenerateLineups(e)}>
            Regenerate Unlocked Lineups
          </button>{" "}
          <button
            onClick={(e) =>
              adjustAllLineupLocks(e, data.lineupsForLineupSet.edges, true)
            }
          >
            Lock All Lineups
          </button>{" "}
          <button
            onClick={(e) =>
              adjustAllLineupLocks(e, data.lineupsForLineupSet.edges, false)
            }
          >
            Unlock All Lineups
          </button>{" "}
        </LoadingOverlay>
      </div>

      <React.Suspense fallback={"Loading summary..."}>
        <LineupSummaries
          lineupSetId={props.lineupSetID}
          numRefetches={props.numRefetches}
        />
      </React.Suspense>
      <br />
      <br />

      <div className="gm-lineups-container">
        {data.lineupsForLineupSet.edges.map((lineupSet, index) => {
          return (
            <GreenMachineLineup
              key={lineupSet.node.id}
              lineupNumber={index + 1}
              lineup={lineupSet.node}
              reloadLineups={reloadLineups}
              commitLock={commitLineupLockStatus}
            />
          );
        })}
      </div>

      {data.lineupsForLineupSet.pageInfo.hasNextPage ? (
        <div className="gm-lineups-spinner">
          <TailSpin color="black" height={50} width={50} />
        </div>
      ) : null}
    </div>
  );
}
